import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FALLBACK_PARENT, type LocationStateWithFrom } from '@/lib/navigation'

/**
 * Contextual back affordance. Prefers the real referrer carried in router state
 * (set by whichever page navigated here), and falls back to a static parent for
 * deep links. Renders nothing on routes with neither.
 */
export function BackButton() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()

  const state = location.state as LocationStateWithFrom | null
  const from = state?.from

  // On the lightbox's own history entry, `from` is inherited from the feed — but the
  // entry BEHIND us is the feed, not `from`. navigate(-1) would land somewhere other
  // than the label promises, so render nothing (the modal covers the header anyway;
  // this only surfaces when the event fails to load and the modal never opens).
  if (state?.lightboxPushed) return null

  // "No `from`" is NOT the same as "deep link". The sidebar navigates with a plain
  // <Link> and no state (Sidebar.tsx:112), so falling back on a missing `from` would
  // offer "Back to Groups" to a user who just clicked Cameras in the nav — for a page
  // they were never on — and then `replace` away their current history entry.
  // react-router stamps the entry index into history.state; idx 0 means this is the
  // first entry in the tab, i.e. genuinely nothing in-app behind us.
  const idx = (window.history.state as { idx?: number } | null)?.idx
  const isDeepLink = (idx ?? 0) === 0

  const target = from ?? (isDeepLink ? FALLBACK_PARENT[location.pathname] : undefined)
  if (!target) return null

  const label = t('actions.backTo', { page: t(target.labelKey as 'nav.cameras') })

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
      onClick={() => {
        // Only step back through history when we know an in-app entry is behind
        // us — that also replays the scroll/filter restore for it. A deep-linked
        // user has no such entry, and navigate(-1) would leave the app entirely.
        if (from) navigate(-1)
        else navigate({ pathname: target.pathname, search: target.search }, { replace: true })
      }}
      aria-label={label}
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
    </Button>
  )
}
